import { useForm } from "@/lib/use-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Field, FieldDescription, FieldGroup } from "@/components/ui/field"
import { LandingPage } from "@/components/landing-page"
import type { LandingUsersPasswordEditProps } from "@/types/generated"

const PasswordEditPage = ({ user, organizationName }: LandingUsersPasswordEditProps) => {
  const { t } = useTranslation()

  const form = useForm({
    "user[reset_password_token]": user.resetPasswordToken,
    "user[password]": "",
    "user[password_confirmation]": ""
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.put("/users/password", { forceFormData: true })
  }

  const allErrors = Object.values(user.errors ?? {}).flat()

  return (
    <LandingPage organizationName={organizationName}>
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">{t("passwords.edit.title")}</h1>
        </div>

        {allErrors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ul className="list-inside list-disc">
              {allErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <Label htmlFor="password_edit_password">{t("passwords.edit.password")}</Label>
              <Input
                id="password_edit_password"
                type="password"
                autoComplete="new-password"
                value={form.data["user[password]"]}
                onChange={(e) => form.setData("user[password]", e.target.value)}
                required
              />
              <FieldDescription>{t("passwords.edit.password_help")}</FieldDescription>
            </Field>

            <Field>
              <Label htmlFor="password_edit_password_confirmation">{t("passwords.edit.password_confirmation")}</Label>
              <Input
                id="password_edit_password_confirmation"
                type="password"
                autoComplete="new-password"
                value={form.data["user[password_confirmation]"]}
                onChange={(e) => form.setData("user[password_confirmation]", e.target.value)}
                required
              />
            </Field>

            <Button type="submit" className="w-full" disabled={form.processing}>
              {form.processing ? t("passwords.edit.submitting") : t("passwords.edit.submit")}
            </Button>
          </FieldGroup>
        </form>
      </div>
    </LandingPage>
  )
}

export default PasswordEditPage
